import { For, Show, createMemo, createSignal, onMount } from "solid-js";
import ManeuverScatter from "../../charts/ManeuverScatter";
import { getManeuverChannelDisplayLabel } from "../../../utils/maneuverChannelLabels";
import { MANEUVER_BASELINE_DERIVED_CHANNELS } from "../../../utils/maneuverBaselineLossColumns";
import { tabledata, eventType, phase } from "../../../store/globalStore";
import { setTriggerUpdate } from "../../../store/selectionStore";
import { TAKEOFF_SCATTER_CHANNELS, getManeuverScatterChannelsForEventType } from "../../../utils/maneuversConfig";
import { createPerformanceChartsScrollRestore } from "../../../utils/performanceChartsScrollRestore";

/** Chart order for scatter view. List channel names in desired display order; channels not listed appear after. */
const charts_order: string[] = getManeuverScatterChannelsForEventType('TACK', 'FULL');

/** Chart order for TAKEOFF scatter: big-table columns minus the TWS ones (already the x-axis). */
const charts_order_takeoff: string[] = [...TAKEOFF_SCATTER_CHANNELS];

const ACCMAX_MSG = "Value at time of maximum acceleration.";

/** Info/warning messages per channel (key = lowercase channel name). Only channels with a non-empty message show an icon. */
const scatter_info: Record<string, { type?: 'info' | 'warning'; message: string }> = {
  mmg: { message: "Meters made good into the wind for tacks, away from the wind in gybes." },
  vmg_perc_avg: { message: "VMG percent of target average." },
  loss_total_tgt: { message: "Overall maneuver loss in meters relative to target vmg." },
  loss_inv_tgt: { message: "Loss during investment phase when entering the maneuver, relative to target vmg." },
  loss_turn_tgt: { message: "Loss during the turn phase of the maneuver, relative to target vmg." },
  loss_build_tgt: { message: "Loss during the build phase of the maneuver relative to target vmg. The build is the period after min boat speed." },
  vmg_perc_build: { message: "VMG percent of target for the complete build phase." },
  vmg_build_avg: { message: "Average VMG during the build phase [KPH]." },
  bsp_drop: { message: "Boat speed at the time of the drop button press." },
  bsp_entry: { message: "Boat speed at maneuver entry." },
  bsp_max: { message: "Maximum boat speed during the maneuver." },
  bsp_min: { message: "Minimum boat speed during the maneuver." },
  twa_entry_n: { message: "Normalized true wind angle at maneuver entry." },
  twa_exit_n: { message: "True wind angle at maneuver exit (maximum turn angle for tacks and gybes)." },
  bsp_min_delta: { message: "Drop boat speed - minimum boat speed" },
  twa_drop_n: { message: "Normalized true wind angle at the time of the drop button press." },
  overshoot_perc: { message: "Overshoot is the difference between your maximim turn angle heading and the heading when you are back up to speed. Overshoot percentage is the ratio of overshoot to max turn angle." },
  turn_angle_max: { message: "Maximum turn angle" },
  turn_rate_max: { message: "Maximum turn rate" },
  rud_ang_max: { message: "Maximum rudder angle" },
  lwy_max: { message: "Maximum leeway" },
  decel_slope: { message: "Deceleration slope.  Larger values = greater deceleration. Values of zero mean no deceleration." },
  accel_slope: { message: "Acceleration slope.  Larger values = greater acceleration. Values of zero mean no acceleration." },
  turn_radius: { message: "If the turn were represented by a circle, this value represents the radius of that circle" },
  time_two_boards: { message: "The seconds with both daggerboards nearly fully extended." },
  time_dropping: { message: "The time from when the button has been pressed to when the stow sequence is completed" },
  time_raising: { message: "The time from when the button has been pressed to when the stow sequence is completed" },
  pop_time: { message: "The time from head to wind when CA1 has inverted signs" },
  drop_time: { message: "The time from head to wind when the drop sequence was initiated" },
  raise_time: { message: "The time from head to wind when the raise sequence was initiated" },
  heel_lock: { message: "The heel when the daggerboard drop sequence completed" },
  rake_min_old_turn: { message: "The minimum rake of the outside foil through the turn" },
  rake_max_new_turn: { message: "The maximum rake of the inside foil through the turn" },
  rake_aoa_min: { message: "The minimum rake angle of attack of the outside foil through the turn" },
  rake_removed_time: { message: "Time from head to wind when the outside foil rake crosses its midpoint during the turn" },
  rake_added_time: { message: "Time from head to wind when the inside foil rake crosses its midpoint during the turn" },
  aoa_raise: { message: "The angle of attack of the foil being raised at the time of the raise." },
  cant_drop_tgt: { message: "The cant drop angle target" },
  rh_avg: { message: "Average ride height over the maneuver window, leeward and windward pooled [MM]." },
  rh_min: { message: "Minimum ride height over the maneuver window, leeward and windward pooled [MM]." },
  rh_max: { message: "Maximum ride height over the maneuver window, leeward and windward pooled [MM]." },
  rh_accmax: { message: "Leeward ride height at the time of maximum acceleration [MM]." },
  bsp_accmax: { message: ACCMAX_MSG },
  twa_accmax: { message: ACCMAX_MSG },
  pitch_accmax: { message: ACCMAX_MSG },
  heel_accmax: { message: ACCMAX_MSG },
  rake_accmax: { message: ACCMAX_MSG },
  rud_rake_accmax: { message: ACCMAX_MSG },
  rud_diff_accmax: { message: ACCMAX_MSG },
  cant_accmax: { message: ACCMAX_MSG },
  jib_sheet_pct_accmax: { message: ACCMAX_MSG },
  jib_lead_ang_accmax: { message: ACCMAX_MSG },
  jib_cunno_load_accmax: { message: ACCMAX_MSG },
  wing_clew_pos_accmax: { message: ACCMAX_MSG },
  wing_twist_accmax: { message: ACCMAX_MSG },
  wing_ca1_accmax: { message: ACCMAX_MSG },
};

/** Info messages for TAKEOFF scatter channels. */
const scatter_info_takeoff: Record<string, { type?: 'info' | 'warning'; message: string }> = {
  mmg: { message: "Meters made good." },
  vmg_perc_avg: { message: "VMG percent of target average." },
  bsp_start: { message: "Boat speed at start." },
  abs_twa_start: { message: "True wind angle at the start of the takeoff." },
  abs_twa_entry: { message: "True wind angle at maneuver entry." },
  abs_twa_final: { message: "True wind angle at the end of the takeoff." },
  bsp_min: { message: "Minimum boat speed before the acceleration." },
  bsp_max: { message: "Maximum boat speed over the takeoff window." },
  bsp_build: { message: "Average boat speed over the build phase: from reaching 28 KPH to the end of the takeoff window." },
  twa_build_n: { message: "Average true wind angle over the same build phase as Build Bsp." },
  time_accel: { message: "Time for acceleration." },
  exit_time: { message: "Pop time (exit time)." },
  pop_time: { message: "Build time: seconds from reaching 28 KPH to reaching 32 KPH. Blank when the takeoff never got there." },
  bsp_exit: { message: "Boat speed at exit (pop)." },
  twa_exit: { message: "True wind angle at exit." },
  accel_max: { message: "Maximum acceleration rate [m/s²]." },
  twa_accmax_n: { message: ACCMAX_MSG },
  pitch_accmax: { message: ACCMAX_MSG },
  heel_accmax: { message: ACCMAX_MSG },
  rake_accmax: { message: ACCMAX_MSG },
  rud_rake_accmax: { message: ACCMAX_MSG },
  rud_diff_accmax: { message: ACCMAX_MSG },
  cant_accmax: { message: ACCMAX_MSG },
  jib_sheet_pct_accmax: { message: ACCMAX_MSG },
  jib_lead_ang_accmax: { message: ACCMAX_MSG },
  jib_cunno_load_accmax: { message: ACCMAX_MSG },
  wing_clew_pos_accmax: { message: ACCMAX_MSG },
  wing_twist_accmax: { message: ACCMAX_MSG },
  wing_ca1_accmax: { message: ACCMAX_MSG }
};

interface ScatterProps {
  context?: string;
}

export default function Scatter(_props: ScatterProps) {
  onMount(() => {
    requestAnimationFrame(() => {
      if (tabledata().length > 0) {
        setTriggerUpdate(true);
      }
    });
  });

  const getScatterScrollInner = (): HTMLElement | undefined =>
    document.getElementById('maneuver-plots') ?? document.getElementById('main-content') ?? undefined;

  const channels = createMemo((): string[] => {
    const rows = tabledata();
    if (!rows || rows.length === 0) return [];
    const isTakeoff = (eventType() || '').toUpperCase() === 'TAKEOFF';

    if (isTakeoff) {
      return [...charts_order_takeoff];
    }

    const orderToUse = getManeuverScatterChannelsForEventType(eventType() || 'TACK', phase());
    const sample = rows[0];

    // Create exclude set with lowercase versions for case-insensitive matching
    // Exclude State and Config as they are color-by channels, not visualization channels
    // twa_entry stays excluded (signed tack-side field); twa_entry_n is plottable for roundup/bearaway
    const excludeLower = new Set([
      'datetime','event_id','tws_avg','tws_bin','twa_entry','tack','race_number','leg_number','event','year','source_name','config','state','time','sink_min','grade','tack_side','row_num','twd_delta',
      // Baseline deltas are written onto the rows for the tables; they are not measured channels,
      // and each one left in here would spawn a chart of its own.
      ...MANEUVER_BASELINE_DERIVED_CHANNELS,
    ]);
    
    // Normalize channel names to lowercase and deduplicate using a Set
    // This prevents duplicates from case variations like "Source_name" and "source_name"
    const seenLowercase = new Set<string>();
    const normalizedChannels: string[] = [];
    
    for (const key of Object.keys(sample)) {
      const keyLower = key.toLowerCase();
      // Skip if excluded (case-insensitive) or already seen
      if (excludeLower.has(keyLower) || seenLowercase.has(keyLower)) {
        // Mark as seen even if excluded, to prevent duplicates
        seenLowercase.add(keyLower);
        continue;
      }
      // Add to seen set and include in channels
      seenLowercase.add(keyLower);
      // Use the original case from the first occurrence
      normalizedChannels.push(key);
    }
    
    // When charts_order is set: only draw those channels, in that order. Otherwise draw all.
    if (orderToUse.length > 0) {
      const byLower = new Map(normalizedChannels.map(c => [c.toLowerCase(), c]));
      const ordered: string[] = [];
      for (const name of orderToUse) {
        const key = name.toLowerCase();
        // Use exact key from first row when present; otherwise use lowercase name so scatter can resolve
        // via case-insensitive lookup (API may return e.g. "bsp_min_delta" while order uses "bsp_min_delta")
        const channelKey = byLower.get(key) ?? key;
        ordered.push(channelKey);
      }
      return ordered;
    }
    return normalizedChannels;
  });

  // Zoom state management
  const [zoom, setZoom] = createSignal(false);
  const [selectedChannel, setSelectedChannel] = createSignal<string | null>(null);

  const plotScrollRestore = createPerformanceChartsScrollRestore({
    getZoom: zoom,
    getScrollInner: getScatterScrollInner,
  });

  const handleZoom = (channel: string | null): void => {
    if (channel) {
      plotScrollRestore.captureBeforeEnteringPlotZoom();
      setSelectedChannel(channel);
      setZoom(true);
    } else {
      setSelectedChannel(null);
      setZoom(false);
    }
  };


  return (
    <>
      <Show when={!zoom()}>
        <div id="maneuver-scatter" class="maneuver-scatter-root">
          <div id="maneuver-plots" class="maneuver-plots-performance-layout">
            <For each={channels()}>{(ch) => (
              <div
                class="maneuver-scatter-chart-wrapper"
                data-chart-search-title={getManeuverChannelDisplayLabel(ch, eventType() ?? undefined)}
              >
                <ManeuverScatter
                  channel={ch}
                  eventType={eventType() ?? undefined}
                  zoom={false}
                  handleZoom={handleZoom}
                  infoMessage={((eventType() || '').toUpperCase() === 'TAKEOFF' ? scatter_info_takeoff[ch.toLowerCase()] : scatter_info[ch.toLowerCase()])?.message ?? ""}
                  infoType={((eventType() || '').toUpperCase() === 'TAKEOFF' ? scatter_info_takeoff[ch.toLowerCase()] : scatter_info[ch.toLowerCase()])?.type ?? "info"}
                />
              </div>
            )}</For>
          </div>
        </div>
      </Show>
      <Show when={zoom() && selectedChannel()}>
        <div class="zoom-container maneuver-scatter-zoom-root">
          <div class="maneuver-scatter-zoom-chart-row">
            <div class="maneuver-scatter-zoom-chart-cell">
              <ManeuverScatter
              channel={selectedChannel()!}
              eventType={eventType() ?? undefined}
              zoom={true}
              handleZoom={handleZoom}
              infoMessage={((eventType() || '').toUpperCase() === 'TAKEOFF' ? scatter_info_takeoff[selectedChannel()!.toLowerCase()] : scatter_info[selectedChannel()!.toLowerCase()])?.message ?? ""}
              infoType={((eventType() || '').toUpperCase() === 'TAKEOFF' ? scatter_info_takeoff[selectedChannel()!.toLowerCase()] : scatter_info[selectedChannel()!.toLowerCase()])?.type ?? "info"}
            />
            </div>
          </div>
        </div>
      </Show>
    </>
  );
}
